import {
    Alert,
    Button,
    Checkbox,
    DatePicker,
    Descriptions,
    Divider,
    Form,
    Input,
    List,
    message,
    Select,
    Space,
} from "antd";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axiosInstance from "../../common/api";
import dayjs from "dayjs";
import { filterOption, date2locale } from "../../common/common";
import { WarningTwoTone } from "@ant-design/icons";

const absMonths = 3;
const absThreshold = 0.05;

export default function () {
    const { processId } = useParams();
    const [data, setData] = useState({});
    const [absenteeism, setAbsenteeism] = useState({});
    const navigate = useNavigate();
    const [justifications, setJustifications] = useState([]);
    const [existingJustifications, setExistingJustifications] = useState([]);
    const [form] = Form.useForm();
    const allDay = Form.useWatch("allDay", form);
    const range = Form.useWatch("range", form);

    useEffect(() => {
        axiosInstance
            .get(`leaveRequest/${processId}/storeAbsencesTask`)
            .then(({ data }) => setData(data));

        axiosInstance
            .get("justificationType", { params: { sign: -1 } })
            .then(({ data }) => setJustifications(data));
    }, []);

    useEffect(() => {
        if (data.variables) {
            const vars = data.variables;
            form.setFieldsValue({
                range: [dayjs(vars.startDate), dayjs(vars.endDate)],
                employee: vars.employee.id,
                justificationTypeId: vars.justificationType.id,
                allDay: !vars.hours,
                minutes:
                    vars.hours &&
                    dayjs(dayjs.duration(vars.hours, "hours").format("HH:mm"), "HH:mm"),
            });
            axiosInstance
                .get("justification", {
                    params: {
                        employee: vars.employee.id,
                        startDate: vars.startDate,
                        endDate: vars.endDate,
                        pageSize: 9999,
                    },
                })
                .then(({ data }) => setExistingJustifications(data.data));
        }
    }, [data]);

    const onFinish = (form) => {
        axiosInstance
            .post("leaveRequest/store", {
                taskId: data.id,
                discarded: form.discarded,
                jr: {
                    ...form,
                    startDate: form.range[0].format("YYYY-MM-DD"),
                    endDate: form.range[1].format("YYYY-MM-DD"),
                    allDay: form.allDay,
                    excludeNonWorkingDays: form.excludeNonWorkingDays,
                    minutes: form.minutes
                        ? dayjs.duration(form.minutes.diff(form.minutes.startOf("day"))).asMinutes()
                        : null,
                },
            })
            .then(() => {
                message.success("Salvato con successo");
                navigate(-1);
            });
    };

    const discard = () => {
        console.log("Discard");
    };

    /*useEffect(() => {
        if (open && !data && !!date) {
            const dateStr = date.format("YYYY-MM-DD");
            axiosInstance
                .get("employeeCompact", { params: { startDate: dateStr, endDate: dateStr } })
                .then(({ data }) => setEmployees(data));
        }
    }, [open, data, date]);*/

    return (
        <div style={{ display: "flex", flexDirection: "column", rowGap: 24 }}>
            <Descriptions
                title="Richiesta di assenza"
                layout="vertical"
                colon={false}
                size="small"
                column={{ xs: 1, sm: 1, md: 1, lg: 3, xl: 3, xxl: 3 }}
            >
                <Descriptions.Item label="Dipendente">
                    {data.variables?.employee?.firstName} {data.variables?.employee?.lastName}
                </Descriptions.Item>

                <Descriptions.Item label="Tipologia assenza" span={2}>
                    {data.variables?.justificationType?.name}
                </Descriptions.Item>
                <Descriptions.Item label="Data inizio">
                    {dayjs(data.variables?.startDate).format("DD/MM/YYYY")}
                </Descriptions.Item>
                <Descriptions.Item label="Data fine">
                    {dayjs(data.variables?.endDate).format("DD/MM/YYYY")}
                </Descriptions.Item>
                {data.variables?.hours > 0 && (
                    <Descriptions.Item label="Ore richieste">
                        {data.variables?.hours}
                    </Descriptions.Item>
                )}
                {data.variables?.requestReason && (
                    <Descriptions.Item label="Motivazione" span={3}>
                        {data.variables?.requestReason}
                    </Descriptions.Item>
                )}
            </Descriptions>
            <Divider orientation="left" style={{ marginTop: 10 }}>
                Approvazione
            </Divider>
            <Descriptions
                layout="vertical"
                colon={false}
                size="small"
                column={{ xs: 1, sm: 1, md: 1, lg: 3, xl: 3, xxl: 3 }}
            >
                <Descriptions.Item label="Approvato da">
                    {data.variables?.manager
                        ? data.variables?.manager.firstName + " " + data.variables?.manager.lastName
                        : "Automatico"}
                </Descriptions.Item>
            </Descriptions>

            {existingJustifications.length > 0 && (
                <>
                    <Divider orientation="left" style={{ marginTop: 10 }}>
                        Sovrapposizioni
                    </Divider>
                    <Alert
                        message="Attenzione!!"
                        description="Per le date indicate sono già presenti le assenze indicate qui sotto!!"
                        type="warning"
                        showIcon
                    />
                    <List
                        size="small"
                        bordered
                        dataSource={existingJustifications}
                        renderItem={(item) => (
                            <List.Item key={item.id}>
                                {item.justificationType.name + " il " + date2locale(item.date)}
                            </List.Item>
                        )}
                    />
                </>
            )}

            <Divider orientation="left" style={{ marginTop: 10 }}>
                Salvataggio
            </Divider>

            {(absenteeism?.rate ?? 0) > absThreshold && (
                <div>
                    <Alert
                        showIcon
                        type="warning"
                        message="Attenzione!!"
                        description={
                            <>
                                Tasso di assenteismo del richiedente (giorni di assenza / giorni
                                lavorativi) negli ultimi {absMonths} mesi:{" "}
                                <b>
                                    {((absenteeism?.rate ?? 0) * 100).toLocaleString("it-IT", {
                                        maximumFractionDigits: 2,
                                    })}
                                    %
                                </b>
                            </>
                        }
                    />
                </div>
            )}
            <Form
                id="newAttendanceJustificationForm"
                form={form}
                layout="vertical"
                onFinish={onFinish}
                style={{ marginTop: 20 }}
            >
                <Form.Item
                    name={"range"}
                    label="Date di inizio e fine"
                    help="La data di fine è inclusiva"
                >
                    <DatePicker.RangePicker format="DD/MM/YYYY" />
                </Form.Item>
                <Form.Item name={"employee"} label="Dipendente">
                    <Select
                        disabled={!range}
                        options={[
                            {
                                label:
                                    data?.variables?.employee.firstName +
                                    " " +
                                    data?.variables?.employee.lastName,
                                value: data?.variables?.employee.id,
                            },
                        ]}
                        showSearch
                        filterOption={filterOption}
                    />
                </Form.Item>
                <Form.Item name={"justificationTypeId"} label="Giustificativo">
                    <Select
                        options={justifications.map((x) => ({
                            label: x.id + " - " + x.name,
                            value: x.id,
                        }))}
                        showSearch
                        filterOption={filterOption}
                    />
                </Form.Item>
                <Form.Item
                    name={"excludeNonWorkingDays"}
                    label="Escludi giorni non lavorativi"
                    valuePropName="checked"
                    initialValue={true}
                >
                    <Checkbox />
                </Form.Item>
                <Form.Item
                    name={"allDay"}
                    label="Tutta la giornata"
                    valuePropName="checked"
                    initialValue={true}
                >
                    <Checkbox />
                </Form.Item>
                {!allDay && (
                    <Form.Item name={"minutes"} label="Ore">
                        <DatePicker.TimePicker format="HH:mm" showNow={false} />
                    </Form.Item>
                )}
                <Form.Item style={{ width: "100%", textAlign: "end" }} name="discarded">
                    <div
                        style={{
                            display: "flex",
                            flexDirection: "row",
                            flexWrap: "wrap",
                            placeContent: "end",
                            columnGap: 16,
                        }}
                    >
                        <Button
                            type="primary"
                            danger
                            style={{ width: 200 }}
                            htmlType="submit"
                            onClick={() => {
                                form.setFieldValue("discarded", true);
                            }}
                        >
                            Scarta
                        </Button>
                        <Button type="primary" style={{ width: 200 }} htmlType="submit">
                            Salva
                        </Button>
                    </div>
                </Form.Item>
            </Form>
        </div>
    );
}
